import React, {useCallback} from 'react';
import useTranslation from "~/app/hooks/useTranslation";
import classes from "./style.module.scss";
import Button from '~/app/common/Button';
import {useParams} from 'react-router-dom';
import {LastPeriodicCheck, Machine, PeriodicCheckSaveResponse, Project} from "~/types";
import {route} from "~/app/helpers/route";
import {MACHINES_ROUTES} from "~/machines/Router";
import {MACHINES_DETAILS_ROUTES} from "~/machines/Details/Router";
import {useHistory} from "react-router";
import {useStoreActions, useStoreState} from "~/store";
import {notify} from "~/app/helpers/notify";
import { generateGUID } from '~/app/helpers/guid';
import moment from 'moment';
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import { InspectionTypes } from '~/periodicCheck/store';

export type FooterComponent = React.FC

const Footer: FooterComponent = () => {
    const {t} = useTranslation("periodicCheck");
    const history = useHistory();
    const {id, projectId} = useParams<{ id: Machine['id'], projectId: Project['id'] }>();
    const isChecksDataUpdated = useStoreState(state => state.periodicCheck.isChecksDataUpdated);
    const checksData = useStoreState(state => state.periodicCheck.checksData);
    const inspectionType = useStoreState(state => state.periodicCheck.inspectionType);
    const loading = useStoreState(state => state.periodicCheck.updating);
    const machine = useStoreState(state => state.machine.data);
    const checkEquipment = useStoreState(state => state.periodicCheck.checkEquipment);
    const dailyCheckId = useStoreState(state => state.periodicCheck.dailyCheckId);
    const submittedPending = useStoreState(state => state.periodicCheck.sessionMode === 'submitted_pending');
    const saveMachineId = checkEquipment?.id ?? machine.id;
    const updatePeriodicChecks = useStoreActions(actions => actions.periodicCheck.update);
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const applyLocalLastPeriodicCheck = useStoreActions(actions => actions.machine.applyLocalLastPeriodicCheck);
    const applyLocalPeriodicCheckChecks = useStoreActions(actions => actions.machine.applyLocalPeriodicCheckChecks);
    const user = useStoreState(state => state.user.data);

    const buildPayload = useCallback((isDraft: number) => ({
        machineId: saveMachineId,
        projectId,
        inspectionType,
        checks: checksData,
        guid: generateGUID(),
        isDraft,
        ...(dailyCheckId && { id: dailyCheckId }),
    }), [saveMachineId, projectId, inspectionType, checksData, dailyCheckId]);

    const back = useCallback(() => {
        const destination = inspectionType !== InspectionTypes.ACCEPTANCE_INSPECTION && machine.linkedMachines && machine.linkedMachines.length > 0
            ? route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, { id, projectId })
            : route(MACHINES_ROUTES.details, { id, projectId });
        history.push(destination);
    }, [id, projectId, history, machine.linkedMachines, inspectionType]);

    const savePeriodicCheck = useCallback(
        (isDraft: number) => updatePeriodicChecks(buildPayload(isDraft)),
        [buildPayload, updatePeriodicChecks]
    );

    const persistLocalCheck = useCallback((isDraft: number, response?: PeriodicCheckSaveResponse | void) => {
        const draftId = response?.checkData?.id ?? response?.id ?? dailyCheckId;
        const lastCheck: LastPeriodicCheck = {
            id: draftId ? String(draftId) : `local-${saveMachineId}`,
            userId: user.id,
            userName: user.name,
            date: moment().format('YYYY-MM-DD'),
            ...(isDraft ? { status: PERIODIC_CHECK_DRAFT_STATUS } : { pendingSync: true }),
        };
        applyLocalPeriodicCheckChecks({
            machineId: saveMachineId,
            inspectionType,
            checks: checksData,
            lastCheck,
            projectId,
        });
        applyLocalLastPeriodicCheck({
            machineId: saveMachineId,
            inspectionType,
            lastCheck,
        });
    }, [
        applyLocalLastPeriodicCheck,
        applyLocalPeriodicCheckChecks,
        checksData,
        dailyCheckId,
        inspectionType,
        projectId,
        saveMachineId,
        user.id,
        user.name,
    ]);

    const saveDraftClickHandler = useCallback(() => {
        savePeriodicCheck(1).then(async (response) => {
            const draftId = response?.checkData?.id ?? response?.id ?? dailyCheckId;
            if (response?.queued) {
                persistLocalCheck(1, response);
                notify(t('draftSaved'), 'success');
                return;
            }
            await MachineFetchAction(id);
            applyLocalLastPeriodicCheck({
                machineId: saveMachineId,
                inspectionType,
                lastCheck: {
                    id: String(draftId ?? saveMachineId),
                    userId: user.id,
                    userName: user.name,
                    date: moment().format('YYYY-MM-DD'),
                    status: PERIODIC_CHECK_DRAFT_STATUS,
                },
            });
            notify( draftId ? t('draftSaved') : t(`${inspectionType}.success`), 'success');
        });
    }, [savePeriodicCheck, persistLocalCheck, dailyCheckId, MachineFetchAction, id, t, inspectionType, applyLocalLastPeriodicCheck, saveMachineId, user.id, user.name]);

    const submit = useCallback(() => {
        savePeriodicCheck(0).then(async (response) => {
            if (response?.queued) {
                persistLocalCheck(0, response);
                notify(t('submittedOffline'), 'success');
                return;
            }
            await MachineFetchAction(id);
            applyLocalLastPeriodicCheck({
                machineId: saveMachineId,
                inspectionType,
                lastCheck: {
                    id: String(saveMachineId),
                    userId: user.id,
                    userName: user.name,
                    date: new Date().toISOString(),
                },
            });
            const destination = inspectionType !== InspectionTypes.ACCEPTANCE_INSPECTION && machine.linkedMachines && machine.linkedMachines.length > 0
                ? route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, { id, projectId })
                : route(MACHINES_ROUTES.details, { id, projectId });
            history.push(destination);
            notify(t(`${inspectionType}.success`), 'success');
        });
    }, [savePeriodicCheck, persistLocalCheck, MachineFetchAction, applyLocalLastPeriodicCheck, saveMachineId, machine.linkedMachines, projectId, inspectionType, user.id, user.name, id, history, t]);

    return (
        <div className={classes['footer']}>
            <Button className={`${classes['abort']}`} onClick={back}> {t('cancel')} </Button>
            {!submittedPending && (
                <>
                    <Button
                        loading={loading}
                        className={classes['temporary']}
                        onClick={saveDraftClickHandler}
                    > {t('temporary')} </Button>
                    <Button
                        loading={loading}
                        className={classes['submit']}
                        onClick={submit}
                        disabled={!isChecksDataUpdated}
                    >  {t('complete')}  </Button>
                </>
            )}
        </div>
    );
};

export default Footer
